"use client";

import { useEffect } from "react";

/**
 * Register the PWA service worker exactly once on the client — production only.
 *
 * In dev mode the SW is actively harmful: `sw.js` does cache-first on
 * `/_next/static/*`, and Next.js dev reuses chunk URLs across rebuilds (no
 * content-hash like prod). After a `.next/` wipe + dev restart, the SSR
 * serves new code while the SW hands back old chunks for the same URLs,
 * which surfaces as hydration mismatches that look unrelated to caching
 * (missing list items, wrong icons, etc.). Skip registration in dev AND
 * tear down any SW + caches the browser is already holding so existing
 * tabs self-heal on the next reload. One-shot location.reload() is gated
 * by sessionStorage so we don't loop.
 *
 * Failure modes:
 *   - No SW support (older Safari, Firefox in private mode): silently
 *     no-op. The rest of the app keeps working as a plain SPA.
 *   - Registration fails (prod): log to console; do NOT surface to user.
 */
export function SwRegister(): null {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator)) return;

    if (process.env.NODE_ENV !== "production") {
      let reloadNeeded = false;
      navigator.serviceWorker
        .getRegistrations()
        .then((regs) =>
          Promise.all(
            regs.map((r) => {
              reloadNeeded = true;
              return r.unregister();
            }),
          ),
        )
        .catch(() => undefined)
        .then(() => {
          if (!("caches" in window)) return;
          return caches
            .keys()
            .then((keys) =>
              Promise.all(
                keys.map((k) => {
                  reloadNeeded = true;
                  return caches.delete(k);
                }),
              ),
            )
            .catch(() => undefined);
        })
        .then(() => {
          if (!reloadNeeded) return;
          const KEY = "vc:sw-cleanup-reloaded";
          if (sessionStorage.getItem(KEY)) return;
          sessionStorage.setItem(KEY, "1");
          // eslint-disable-next-line no-console
          console.warn(
            "[pwa] dev-mode SW + caches cleared; reloading once for a clean bundle",
          );
          window.location.reload();
        });
      return;
    }

    navigator.serviceWorker.register("/sw.js").catch((err) => {
      // eslint-disable-next-line no-console
      console.warn("[pwa] sw register failed:", err);
    });
  }, []);
  return null;
}
